import { z } from 'zod';
import { isoDaySchema } from './dates';
import { paginatedSchema, paginationQuerySchema } from './pagination';

// ---------------------------------------------------------------------------------------------
// Alertas e desvios (PRD §11.26, RF-40, RN-12, RN-13, RN-15; DEC-044).

/** Tipos de alerta (PRD §11.26). */
export const ALERT_TYPES = [
  'KPI_BELOW_TARGET',
  'CONSECUTIVE_DROP',
  'ACTION_LATE',
  'OBJECTIVE_AT_RISK',
  'TARGET_REACHED',
  'DATA_STALE',
  'MANUAL_DEVIATION',
] as const;
export type AlertTypeName = (typeof ALERT_TYPES)[number];
/** Tipos que o motor avalia (e resolve sozinho); o desvio informado é de uma pessoa (T-094b; DEC-045). */
export const ALERT_ENGINE_TYPES: readonly AlertTypeName[] = [
  'KPI_BELOW_TARGET',
  'CONSECUTIVE_DROP',
  'ACTION_LATE',
  'OBJECTIVE_AT_RISK',
  'TARGET_REACHED',
  'DATA_STALE',
];
export const ALERT_TYPE_LABELS: Record<AlertTypeName, string> = {
  KPI_BELOW_TARGET: 'KPI abaixo da meta',
  CONSECUTIVE_DROP: 'Queda consecutiva',
  ACTION_LATE: 'Ação atrasada',
  OBJECTIVE_AT_RISK: 'Objetivo em risco',
  TARGET_REACHED: 'Meta atingida',
  DATA_STALE: 'Dado não atualizado',
  MANUAL_DEVIATION: 'Desvio informado',
};
/** Tipos cujo assunto é um KPI (os demais: ação atrasada → ação; objetivo em risco → objetivo). */
export const KPI_ALERT_TYPES: readonly AlertTypeName[] = [
  'KPI_BELOW_TARGET',
  'CONSECUTIVE_DROP',
  'TARGET_REACHED',
  'DATA_STALE',
];

/** Criticidade (calculada pelo motor — DEC-044). */
export const ALERT_CRITICALITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type AlertCriticalityName = (typeof ALERT_CRITICALITIES)[number];
export const ALERT_CRITICALITY_LABELS: Record<AlertCriticalityName, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

/** Ciclo do alerta: aberto → em tratamento → resolvido ou descartado. */
export const ALERT_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'DISMISSED'] as const;
export type AlertStatusName = (typeof ALERT_STATUSES)[number];
export const ALERT_STATUS_LABELS: Record<AlertStatusName, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em tratamento',
  RESOLVED: 'Resolvido',
  DISMISSED: 'Descartado',
};
/** Status em que o alerta ainda pede atenção (e bloqueia um alerta equivalente novo). */
export const ALERT_OPEN_STATUSES: readonly AlertStatusName[] = ['OPEN', 'IN_PROGRESS'];
/** Status encerrados (não reabrem). */
export const ALERT_CLOSED_STATUSES: readonly AlertStatusName[] = ['RESOLVED', 'DISMISSED'];

/** Chave da deduplicação: um alerta aberto por tipo e assunto (KPI, objetivo ou ação). */
export const alertSubjectKey = (type: AlertTypeName, subjectId: string) => `${type}:${subjectId}`;

// ---------------------------------------------------------------------------------------------
// Parâmetros da empresa

/** Padrões (DEC-044): queda em 3 períodos, objetivo em risco com 2 KPIs vermelhos, tolerância de 10 dias. */
export const DEFAULT_ALERT_SETTINGS = {
  consecutiveDropPeriods: 3,
  objectiveRedKpis: 2,
  staleGraceDays: 10,
} as const;
export const ALERT_SETTINGS_LIMITS = {
  consecutiveDropPeriods: { min: 2, max: 12 },
  objectiveRedKpis: { min: 1, max: 20 },
  staleGraceDays: { min: 0, max: 60 },
} as const;

export const alertSettingsSchema = z.object({
  consecutiveDropPeriods: z.number().int(),
  objectiveRedKpis: z.number().int(),
  staleGraceDays: z.number().int(),
  /** null = nunca alterados (valem os padrões). */
  updatedAt: z.string().nullable(),
});
export type AlertSettings = z.infer<typeof alertSettingsSchema>;

/** Número inteiro; texto numérico (formulário) é aceito, mas vazio, nulo e booleano não viram 0 ou 1. */
const intField = (label: string, { min, max }: { min: number; max: number }) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
    z
      .number({ message: `Informe ${label} em números.` })
      .int('Use um número inteiro.')
      .min(min, `${label[0]!.toUpperCase()}${label.slice(1)} vai de ${min} a ${max}.`)
      .max(max, `${label[0]!.toUpperCase()}${label.slice(1)} vai de ${min} a ${max}.`),
  );

export const alertSettingsInputSchema = z.object({
  consecutiveDropPeriods: intField(
    'a quantidade de períodos da queda',
    ALERT_SETTINGS_LIMITS.consecutiveDropPeriods,
  ),
  objectiveRedKpis: intField('a quantidade de KPIs vermelhos', ALERT_SETTINGS_LIMITS.objectiveRedKpis),
  staleGraceDays: intField('a tolerância em dias', ALERT_SETTINGS_LIMITS.staleGraceDays),
});
export type AlertSettingsInput = z.input<typeof alertSettingsInputSchema>;

// ---------------------------------------------------------------------------------------------
// Ciclo do alerta (T-110b)

const ref = z.object({ id: z.string().uuid(), name: z.string() });

export const alertSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(ALERT_TYPES),
  criticality: z.enum(ALERT_CRITICALITIES),
  status: z.enum(ALERT_STATUSES),
  title: z.string(),
  message: z.string(),
  /** Números da avaliação (performance, KPIs vermelhos, dias de atraso...). */
  details: z.record(z.string(), z.unknown()),
  /** Período a que o alerta se refere (AAAA-MM-DD). */
  periodStart: z.string(),
  periodEnd: z.string(),
  /** Assunto: um dos três, conforme o tipo. */
  kpi: ref.extend({ archived: z.boolean() }).nullable(),
  objective: ref.extend({ archived: z.boolean() }).nullable(),
  action: z.object({ id: z.string().uuid(), title: z.string(), planId: z.string().uuid() }).nullable(),
  perspective: ref.nullable(),
  department: ref.nullable(),
  businessUnit: ref.nullable(),
  responsible: ref.nullable(),
  /** Encerramento: justificativa, automático ou não, quando e quem. */
  resolution: z.string().nullable(),
  autoResolved: z.boolean(),
  closedAt: z.string().nullable(),
  closedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Planos de ação gerados a partir do alerta (RN-13), os que quem pede enxerga. */
  plans: z.array(ref.extend({ archived: z.boolean() })),
});
export type Alert = z.infer<typeof alertSchema>;
export const alertListSchema = paginatedSchema(alertSchema);
export type AlertList = z.infer<typeof alertListSchema>;

/** Situação: os que pedem atenção (aberto e em tratamento) ou os encerrados. */
export const ALERT_STATES = ['open', 'closed'] as const;

export const alertListQuerySchema = paginationQuerySchema.extend({
  type: z.enum(ALERT_TYPES).optional(),
  status: z.enum(ALERT_STATUSES).optional(),
  state: z.enum(ALERT_STATES).optional(),
  criticality: z.enum(ALERT_CRITICALITIES).optional(),
  perspectiveId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
  responsibleId: z.string().uuid().optional(),
  kpiId: z.string().uuid().optional(),
  objectiveId: z.string().uuid().optional(),
  /** Unidade do alerta (drill-down até a unidade — T-144a). */
  businessUnitId: z.string().uuid().optional(),
  /** Período do alerta que cruza o intervalo (inclusive). */
  periodFrom: isoDaySchema.optional(),
  periodTo: isoDaySchema.optional(),
});
export type AlertListQuery = z.input<typeof alertListQuerySchema>;

/**
 * Para onde o alerta pode ir (DEC-044): aberto → em tratamento (assumir) → resolvido ou descartado; de aberto
 * também direto para resolvido ou descartado. Não volta: em tratamento não reabre e encerrado não muda.
 */
export const ALERT_STATUS_TARGETS = ['IN_PROGRESS', 'RESOLVED', 'DISMISSED'] as const;
/** Justificativa ao encerrar (resolver ou descartar) — obrigatória; ao assumir, opcional. */
export const ALERT_JUSTIFICATION_MAX = 1000;
export const alertStatusInputSchema = z
  .object({
    status: z.enum(ALERT_STATUS_TARGETS, { message: 'Escolha o novo status.' }),
    justification: z
      .string()
      .trim()
      .max(
        ALERT_JUSTIFICATION_MAX,
        `A justificativa pode ter no máximo ${ALERT_JUSTIFICATION_MAX} caracteres.`,
      )
      .nullable()
      .optional()
      .transform((v) => v || null),
  })
  .refine((v) => !ALERT_CLOSED_STATUSES.includes(v.status) || (v.justification?.length ?? 0) >= 5, {
    message: 'Explique por que o alerta foi resolvido ou descartado (pelo menos 5 caracteres).',
    path: ['justification'],
  });
export type AlertStatusInput = z.input<typeof alertStatusInputSchema>;

// ---------------------------------------------------------------------------------------------
// Desvio informado (T-094b, RF-52, PRD §9.5 "informar desvios"; DEC-045)

export const DEVIATION_SUBJECTS = ['KPI', 'OBJECTIVE'] as const;
export type DeviationSubjectName = (typeof DEVIATION_SUBJECTS)[number];

export const deviationInputSchema = z
  .object({
    kpiId: z
      .string()
      .uuid('Indicador inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    objectiveId: z
      .string()
      .uuid('Objetivo inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    /** Unidade onde o desvio aconteceu (opcional; só no desvio de indicador — T-144a, DEC-058). */
    businessUnitId: z
      .string()
      .uuid('Unidade inválida.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    criticality: z.enum(ALERT_CRITICALITIES, { message: 'Escolha a criticidade.' }),
    description: z
      .string()
      .trim()
      .min(10, 'Descreva o desvio (pelo menos 10 caracteres).')
      .max(ALERT_JUSTIFICATION_MAX, `A descrição pode ter no máximo ${ALERT_JUSTIFICATION_MAX} caracteres.`),
    /** Data a que o desvio se refere (AAAA-MM-DD; até hoje). */
    referenceDate: isoDaySchema,
  })
  .refine((v) => (v.kpiId === null) !== (v.objectiveId === null), {
    message: 'Escolha o indicador ou o objetivo do desvio.',
    path: ['subject'],
  })
  .refine((v) => v.businessUnitId === null || v.kpiId !== null, {
    message: 'A unidade só vale para o desvio de um indicador.',
    path: ['businessUnitId'],
  });
export type DeviationInput = z.input<typeof deviationInputSchema>;

/** KPIs e objetivos ativos no escopo de quem informa (opções do formulário). */
export const deviationOptionsSchema = z.object({
  kpis: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
  objectives: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** Unidades ativas da empresa (onde o desvio do indicador aconteceu). */
  businessUnits: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type DeviationOptions = z.infer<typeof deviationOptionsSchema>;

// ---------------------------------------------------------------------------------------------
// Central de alertas (T-111; DEC-046)

const optionRef = z.object({ id: z.string().uuid(), name: z.string() });
/** Opções dos filtros da central, no escopo de quem pede. */
export const alertFilterOptionsSchema = z.object({
  perspectives: z.array(optionRef),
  departments: z.array(optionRef),
  people: z.array(optionRef),
  kpis: z.array(optionRef.extend({ code: z.string() })),
  /** Unidades dos alertas (desvio informado numa unidade, ação de plano da unidade). */
  businessUnits: z.array(optionRef),
});
export type AlertFilterOptions = z.infer<typeof alertFilterOptionsSchema>;

/** Contador do menu: alertas abertos (aberto ou em tratamento) dos projetos em andamento, no escopo. */
export const alertOpenCountSchema = z.object({
  open: z.number().int(),
  /** Por projeto (a central mostra um projeto por vez e avisa dos abertos nos outros). */
  projects: z.array(z.object({ projectId: z.string().uuid(), open: z.number().int() })),
});
